/// <amd-module name="Wit/Common/QuerySaveDialog/Components/QuerySaveDialog" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { FormItem } from "VSSUI/FormItem";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { TextField } from "VSSUI/TextField";
import { KeyCode } from "VSSUI/Util";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { QueryHierarchyItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IQueryListService } from "Wit/Common/QueryListServices/IQueryListService";
import { IQuerySaveDialogProps } from "Wit/Common/QuerySaveDialog/Components/QuerySaveDialog.Props";
import * as Resources from "Wit/Common/QuerySaveDialog/Resources";
import { QueryItem, QuerySaveDialogMode, EmptyItem } from "Wit/Common/QueryUtils/Models";
import { reportError } from "Wit/Common/QueryUtils/ErrorHandlingUtils";
import { QueryItemDropdown } from "./QueryItemDropdown";

/** Regex used to identify backslashes in path */
const c_backslashRegex = /\\/g;
/** Default to debounce 400ms */
const c_defaultDebounceWait = 400;
/** Regex used to identify empty path segments in path*/
const c_emptyPathRegex = /[\/]+/g;
/** Maximum number of characters in name */
const c_maxQueryNameLength = 255;
/** Regex to identify invalid characters in name */
const c_nameRegex = new RegExp(/^[^\/\\<>*?\"|:]*$/);
/** Regex to identify trailing slashes */
const c_trailingSlashRegex = /\/+$/;

export class QuerySaveDialog extends VssComponent<IQuerySaveDialogProps> {
    private _debouncedQueryNameValidation: (queryName: string) => void;
    private _debouncedQueryFolderValidation: (folderPath: string) => void;
    private _listService: IQueryListService;
    private _name: ObservableValue<string> = new ObservableValue("");
    private _nameValidationMessage: ObservableValue<string | undefined> = new ObservableValue(undefined);
    private _nameValidationError: ObservableValue<boolean> = new ObservableValue(false);
    private _path: ObservableValue<string> = new ObservableValue("");
    private _pathValidationMessage: ObservableValue<string | undefined> = new ObservableValue(undefined);
    private _pathValidationError: ObservableValue<boolean> = new ObservableValue(false);
    private _timerManagement: TimerManagement;
    private _errorMessage: ObservableValue<string | undefined> = new ObservableValue(undefined);

    constructor(props: IQuerySaveDialogProps, context: IVssComponentContext) {
        super(props, context);

        if (this.props.queryItem) {
            this._path.value = this._getQueryParentPath() ?? "";
        }

        this._name.value = this.props.mode === QuerySaveDialogMode.NewFolder ? "" : this.props.queryItem?.name ?? "";
        if (!this._path.value) {
            this._path.value = Resources.MyQueries;
        }

        this._listService = this.context.pageContext.getService<IQueryListService>("IQueryListService");
        this._timerManagement = new TimerManagement();
        this._debouncedQueryNameValidation = this._timerManagement.debounce(this._performQueryNameValidation, c_defaultDebounceWait);
        this._debouncedQueryFolderValidation = this._timerManagement.debounce(this._performFolderPathValidation, c_defaultDebounceWait);
    }

    public componentDidMount(): void {
        if (this._name.value) this._debouncedQueryNameValidation(this._name.value);
    }

    public render(): JSX.Element {
        return (
            <Observer
                name={this._name}
                nameHasError={this._nameValidationError}
                path={this._path}
                pathHasError={this._pathValidationError}
                errorMessage={this._errorMessage}
            >
                {({ name, nameHasError, path, pathHasError, errorMessage }) => {
                    const fullPath = `${path}/${name}`;
                    return (
                        <Dialog
                            titleProps={{ text: this._getTitleFromMode(this.props.mode) }}
                            onDismiss={() => this.onDismiss()}
                            footerButtonProps={[
                                {
                                    text: Resources.Cancel,
                                    onClick: () => this.onDismiss()
                                },
                                {
                                    text: Resources.OK,
                                    disabled:
                                        !name ||
                                        nameHasError ||
                                        pathHasError ||
                                        !!errorMessage ||
                                        ((this.props.mode === QuerySaveDialogMode.SaveAs || this.props.mode === QuerySaveDialogMode.RenameQuery) &&
                                            fullPath === this.props.queryItem?.path),
                                    onClick: () => this._save(),
                                    primary: true
                                }
                            ]}
                        >
                            {errorMessage && (
                                <MessageCard className="margin-bottom-8" severity={MessageCardSeverity.Error}>
                                    {errorMessage}
                                </MessageCard>
                            )}
                            <FormItem 
                                label={Resources.NameLabel} 
                                message={this._nameValidationMessage} 
                                error={this._nameValidationError} 
                                required={true}
                            >
                                <TextField
                                    onKeyDown={this._onKeyDown}
                                    placeholder={Resources.NameLabel_Watermark}
                                    value={this._name}
                                    onChange={(event, value) => {
                                        this._name.value = value;
                                        this._debouncedQueryFolderValidation(this._path.value);
                                        this._debouncedQueryNameValidation(value);
                                    }}
                                    required={true}
                                />
                            </FormItem>

                            <FormItem
                                className="padding-top-16"
                                label={Resources.NewFolder}
                                message={this._pathValidationMessage}
                                error={this._pathValidationError}
                                required={true}
                            >
                                <QueryItemDropdown
                                    onSetFolder={path => {
                                        if (path) {
                                            this._setPath(path);
                                        }
                                    }}
                                    selectedText={this._path}
                                />
                            </FormItem>
                        </Dialog>
                    );
                }}
            </Observer>
        );
    }

    private onDismiss() {
        if (this.props.onDismiss) {
            this.props.onDismiss();
        }
    }

    private async _createQuery(queryItem: QueryItem, parentPath: string): Promise<QueryHierarchyItem | undefined> {
        return await this._listService.createQuery(queryItem as QueryHierarchyItem, parentPath, this.props.navigateToQueryOnSave);
    }

    private _normalizePath(path: string | undefined) {
        return (
            path!
                // Backslashes to forward slashes
                .replace(c_backslashRegex, "/")
                // Remove empty path segments (a//b => a/b)
                .replace(c_emptyPathRegex, "/")
                // Remove trailing slashes
                .replace(c_trailingSlashRegex, "")
        );
    }

    private _onKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
        if (e.keyCode === KeyCode.enter) {
            if (!this._nameValidationError.value && !this._pathValidationError.value) {
                this._save();

                e.preventDefault();
                e.stopPropagation();
            }
        }
    };

    private _performFolderPathValidation = async (folderPath: string): Promise<void> => {
        folderPath = folderPath.trim();

        if (!folderPath) {
            this._pathValidationMessage.value = Resources.QueryFolderPicker_ErrorMessage_FolderRequired;
            this._pathValidationError.value = true;
        } else {
            const queryItem = this._listService.queryTree.value.filter((query: ITreeItemEx<QueryItem | EmptyItem>) => {
                return !query.underlyingItem.data.isEmptyFolderContext && query.underlyingItem.data.path === folderPath;
            });
            if (!queryItem || queryItem.length === 0) {
                try {
                    await this._listService.getQueryByPath(folderPath);
                    this._pathValidationMessage.value = undefined;
                    this._pathValidationError.value = false;
                    this._errorMessage.value = undefined;
                    return;
                } catch (error) {
                    this._pathValidationError.value = true;
                    this._pathValidationMessage.value = Resources.QueryFolderPicker_ErrorMessage_FolderDoesNotExist;
                    return;
                }
            } else {
                this._pathValidationMessage.value = undefined;
                this._pathValidationError.value = false;
                return;
            }
        }
    };

    private _performQueryNameValidation = async (queryName: string): Promise<void> => {
        queryName = queryName && queryName.trim();

        if (!queryName) {
            this._nameValidationError.value = true;
            (this._nameValidationMessage.value = Resources.QuerySaveDialog_ErrorMessage_NameRequired), null;
        } else if (!c_nameRegex.test(queryName)) {
            this._nameValidationError.value = true;
            (this._nameValidationMessage.value = Resources.QuerySaveDialog_ErrorMessage_NameInvalid), null;
        } else if (queryName.length > c_maxQueryNameLength) {
            this._nameValidationError.value = true;
            this._nameValidationMessage.value = format(
                Resources.QueryFolderPicker_ErrorMessage_NameExceededLimit,
                c_maxQueryNameLength,
                queryName.length
            );
        } else if (!this._pathValidationError.value) {
            const queryPath = `${this._path.value}/${queryName}`;
            // Validate if the path exist. This needs to go to the server if the query item not populated in local store.
            const queryItem = this._listService.queryTree.value.filter((query: ITreeItemEx<QueryItem | EmptyItem>) => {
                !query.underlyingItem.data.isEmptyFolderContext && query.underlyingItem.data.path === queryPath;
            });
            if (!queryItem || queryItem.length === 0) {
                try {
                    await this._listService.getQueryByPath(queryPath);
                } catch (error) {
                    this._nameValidationMessage.value = undefined;
                    this._nameValidationError.value = false;
                    return;
                }
            }

            this._nameValidationError.value = true;
            this._nameValidationMessage.value = Resources.QuerySaveDialog_ErrorMessage_NameAlreadyExists;
        }
    };

    private async _save() {
        const name = this._name.value.trim();
        const folderPath = this._normalizePath(this._path.value).trim();
        const originalQueryItem = this.props.queryItem;

        switch (this.props.mode) {
            case QuerySaveDialogMode.NewFolder:
            case QuerySaveDialogMode.NewQuery:
            case QuerySaveDialogMode.SaveAs:
                const isNewFolder = this.props.mode === QuerySaveDialogMode.NewFolder;
                const updatedQueryItem = {
                    name: name,
                    wiql: originalQueryItem ? originalQueryItem.wiql : undefined,
                    isFolder: originalQueryItem ? originalQueryItem.isFolder : isNewFolder,
                    id: undefined, // If id is Guid empty then it is create else it is update query
                    path: `${folderPath}/${name}`
                } as unknown as QueryItem;

                let queryHierarchyItem;
                try {
                    queryHierarchyItem = await this._createQuery(updatedQueryItem, folderPath);
                } catch (error) {
                    this._errorMessage.value = error.message;
                    return;
                }

                if (this.props.onSave && queryHierarchyItem) {
                    this.props.onSave(queryHierarchyItem.id);
                }
                break;
            case QuerySaveDialogMode.RenameFolder:
            case QuerySaveDialogMode.RenameQuery:
                const defaultFolderPath = this._normalizePath(this._getQueryParentPath()).trim();

                // If the user changed path, then it is a move operation.
                // Else it is an update operation
                if (defaultFolderPath !== folderPath) {
                    const originalQueryTreeItem = this._listService.queryTree.value.find((treeItem: ITreeItemEx<QueryItem | EmptyItem>) => {
                        return !treeItem.underlyingItem.data.isEmptyFolderContext && treeItem.underlyingItem.data.id === this.props.queryItem?.id;
                    }) as ITreeItemEx<QueryItem>;
                    if (!originalQueryTreeItem) {
                        return;
                    }

                    let parentTreeItem = this._listService.queryTree.value.find((treeItem: ITreeItemEx<QueryItem | EmptyItem>) => {
                        return !treeItem.underlyingItem.data.isEmptyFolderContext && treeItem.underlyingItem.data.path === folderPath;
                    })?.underlyingItem;

                    if (!parentTreeItem) {
                        parentTreeItem = await this._listService.getTreeItemByPath(folderPath);
                    }
                    
                    if (originalQueryTreeItem && parentTreeItem) {
                        const movedQuery = await this._listService.moveQuery(originalQueryTreeItem, parentTreeItem, name);
                        if (this.props.onRename) {
                            this.props.onRename(movedQuery);
                        }
                    } else {
                        reportError(this.context.pageContext, "rename-item", Resources.QuerySaveDialog_ErrorMessage_MoveHasFailed);
                    }
                } else {
                    const renamedQueryItem = {
                        ...originalQueryItem,
                        name: name,
                        path: `${defaultFolderPath}/${name}`
                    } as QueryItem;
                    let valuedIndex = this._listService.queryFolderItem.value?.children.findIndex(queryItem => {
                        return queryItem.id === originalQueryItem?.id;
                    });
                    if (valuedIndex !== undefined) {
                        if (this._listService.queryFolderItem.value) {
                            this._listService.queryFolderItem.value.children[valuedIndex] = renamedQueryItem;
                        }
                    }

                    const originalQueryTreeItem = this._listService.queryTree.value.find((treeItem: ITreeItemEx<QueryItem | EmptyItem>) => {
                        return !treeItem.underlyingItem.data.isEmptyFolderContext && treeItem.underlyingItem.data.id === this.props.queryItem?.id;
                    }) as ITreeItemEx<QueryItem>;
                    await this._listService.updateQuery(renamedQueryItem, originalQueryTreeItem);
                    if (this.props.onRename) {
                        this.props.onRename(renamedQueryItem);
                    }
                }
                break;
            default:
                break;
        }

        this.onDismiss();
    }

    private _setPath(path: string) {
        const newPath = path || "";
        this._path.value = path;
        this._debouncedQueryFolderValidation(newPath);

        if (this._name) {
            this._debouncedQueryNameValidation(this._name.value);
        }
    }

    private _getTitleFromMode(mode: QuerySaveDialogMode): string {
        switch (mode) {
            case QuerySaveDialogMode.NewFolder:
                return Resources.NewFolder;
            case QuerySaveDialogMode.NewQuery:
                return Resources.NewQueryCommand;
            case QuerySaveDialogMode.RenameQuery:
                return Resources.RenameQueryTitle;
            case QuerySaveDialogMode.RenameFolder:
                return Resources.RenameQueryFolderTitle;
            case QuerySaveDialogMode.SaveAs:
                return Resources.SaveQueryAsDialogTitle;
        }
    }

    private _getQueryParentPath() {
        return this.props.mode === QuerySaveDialogMode.RenameQuery ||
            this.props.mode === QuerySaveDialogMode.RenameFolder ||
            this.props.mode === QuerySaveDialogMode.SaveAs
            ? this.props.queryItem?.path.slice(0, this.props.queryItem?.path.lastIndexOf("/"))
            : this.props.queryItem?.path;
    }
}
